import { createContext, type ReactNode, useCallback, useState } from 'react'
import * as accountSettingsService from '@/services/firebase/accountSettings'
import { useAuth } from '@/store/auth/useAuth'
import { type AccountSettingsContextValue, initialOperationState, type OperationState } from './types'

export const AccountSettingsContext = createContext<AccountSettingsContextValue | undefined>(undefined)

interface AccountSettingsProviderProps {
  children: ReactNode
}

export function AccountSettingsProvider({ children }: AccountSettingsProviderProps) {
  // updateProfile() mutates the Firebase currentUser in place without firing
  // onAuthStateChanged, so the auth store never re-dispatches and header/avatar
  // consumers keep rendering the stale name/photo until an unrelated re-render
  // (e.g. navigation). reloadUser() reloads the user and dispatches AUTH_SUCCESS,
  // pushing the fresh values to every useAuth() consumer immediately.
  const { reloadUser } = useAuth()
  const [displayNameOp, setDisplayNameOp] = useState<OperationState>(initialOperationState)
  const [emailOp, setEmailOp] = useState<OperationState>(initialOperationState)
  const [passwordOp, setPasswordOp] = useState<OperationState>(initialOperationState)
  const [addPasswordOp, setAddPasswordOp] = useState<OperationState>(initialOperationState)
  const [deleteAccountOp, setDeleteAccountOp] = useState<OperationState>(initialOperationState)
  const [removePhotoOp, setRemovePhotoOp] = useState<OperationState>(initialOperationState)
  const [uploadPhotoOp, setUploadPhotoOp] = useState<OperationState>(initialOperationState)

  const updateDisplayName = useCallback(
    async (name: string) => {
      setDisplayNameOp({ loading: true, error: null })
      try {
        await accountSettingsService.updateDisplayName(name)
        await reloadUser()
        setDisplayNameOp({ loading: false, error: null })
        return true
      } catch (err) {
        setDisplayNameOp({ loading: false, error: err instanceof Error ? err.message : 'Unknown error' })
        return false
      }
    },
    [reloadUser]
  )

  const updateEmail = useCallback(
    async (newEmail: string, currentPassword: string) => {
      setEmailOp({ loading: true, error: null })
      try {
        await accountSettingsService.updateEmail(newEmail, currentPassword)
        // The address really has changed on the auth user, but nothing re-dispatches it — without
        // this the Email row keeps showing the old one.
        await reloadUser()
        setEmailOp({ loading: false, error: null })
        return true
      } catch (err) {
        setEmailOp({ loading: false, error: err instanceof Error ? err.message : 'Unknown error' })
        return false
      }
    },
    [reloadUser]
  )

  const updatePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    setPasswordOp({ loading: true, error: null })
    try {
      await accountSettingsService.updatePassword(currentPassword, newPassword)
      setPasswordOp({ loading: false, error: null })
      return true
    } catch (err) {
      setPasswordOp({ loading: false, error: err instanceof Error ? err.message : 'Unknown error' })
      return false
    }
  }, [])

  const addPasswordFn = useCallback(
    async (newPassword: string) => {
      setAddPasswordOp({ loading: true, error: null })
      try {
        await accountSettingsService.addPassword(newPassword)
        // Linking flips providerData, which is what `hasPasswordProvider` reads off.
        await reloadUser()
        setAddPasswordOp({ loading: false, error: null })
        return true
      } catch (err) {
        setAddPasswordOp({ loading: false, error: err instanceof Error ? err.message : 'Unknown error' })
        return false
      }
    },
    [reloadUser]
  )

  const clearAllErrors = useCallback(() => {
    setDisplayNameOp((prev) => ({ ...prev, error: null }))
    setEmailOp((prev) => ({ ...prev, error: null }))
    setPasswordOp((prev) => ({ ...prev, error: null }))
    setAddPasswordOp((prev) => ({ ...prev, error: null }))
  }, [])

  const removePhotoFn = useCallback(async () => {
    setRemovePhotoOp({ loading: true, error: null })
    try {
      await accountSettingsService.removeProfilePhoto()
      await reloadUser()
      setRemovePhotoOp({ loading: false, error: null })
      return true
    } catch (err) {
      setRemovePhotoOp({ loading: false, error: err instanceof Error ? err.message : 'Unknown error' })
      return false
    }
  }, [reloadUser])

  const uploadPhotoFn = useCallback(
    async (blob: Blob) => {
      setUploadPhotoOp({ loading: true, error: null })
      try {
        await accountSettingsService.uploadProfilePhoto(blob)
        await reloadUser()
        setUploadPhotoOp({ loading: false, error: null })
        return true
      } catch (err) {
        setUploadPhotoOp({ loading: false, error: err instanceof Error ? err.message : 'Unknown error' })
        return false
      }
    },
    [reloadUser]
  )

  const deleteAccountFn = useCallback(async () => {
    setDeleteAccountOp({ loading: true, error: null })
    try {
      const feedbackToken = await accountSettingsService.deleteAccount()
      setDeleteAccountOp({ loading: false, error: null })
      return feedbackToken
    } catch (err) {
      setDeleteAccountOp({ loading: false, error: err instanceof Error ? err.message : 'Unknown error' })
      return false
    }
  }, [])

  return (
    <AccountSettingsContext.Provider
      value={{
        displayName: displayNameOp,
        email: emailOp,
        password: passwordOp,
        addPassword: addPasswordOp,
        deleteAccount: deleteAccountOp,
        removePhoto: removePhotoOp,
        uploadPhoto: uploadPhotoOp,
        updateDisplayName,
        updateEmail,
        updatePassword,
        addPasswordFn,
        deleteAccountFn,
        removePhotoFn,
        uploadPhotoFn,
        clearAllErrors
      }}>
      {children}
    </AccountSettingsContext.Provider>
  )
}
